// src/pages/ChangePasswordPage.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Lock, CheckCircle, AlertCircle } from 'lucide-react';
import './ChangePasswordPage.css';

const ChangePasswordPage: React.FC = () => {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        // Validações frontend
        if (newPassword !== confirmPassword) {
            setError('As senhas não coincidem');
            return;
        }

        if (newPassword.length < 6) {
            setError('Nova senha deve ter no mínimo 6 caracteres');
            return;
        }

        if (currentPassword === newPassword) {
            setError('Nova senha deve ser diferente da senha atual');
            return;
        }

        setLoading(true);

        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch('/api/password/change-password', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    currentPassword,
                    newPassword
                })
            });

            const data = await response.json();

            if (response.ok) {
                setSuccess(true);

                // Aguardar 2 segundos e fazer logout
                setTimeout(() => {
                    logout();
                    navigate('/login', {
                        state: { message: 'Senha alterada com sucesso. Faça login novamente.' }
                    });
                }, 2000);
            } else {
                setError(data.message || 'Erro ao trocar senha');
            }
        } catch (err) {
            setError('Erro ao conectar ao servidor');
        } finally {
            setLoading(false);
        }
    };

    // Se usuário usa Google, mostrar mensagem
    if (user?.authProvider === 'google' || user?.authProvider === 'local+google') {
        return (
            <div className="cpp-page">
                <header className="cpp-header">
                    <button className="cpp-btn-back" onClick={() => navigate('/student-area')}>
                        <ArrowLeft size={20} />
                        Voltar
                    </button>
                    <div className="cpp-header-logo">
                        <h1>GRAIA</h1>
                    </div>
                </header>

                <div className="cpp-container">
                    <div className="cpp-card">
                        <div className="cpp-icon-warning">
                            <AlertCircle size={48} />
                        </div>
                        <h2>Conta vinculada ao Google</h2>
                        <p className="cpp-google-message">
                            Sua conta está vinculada ao Google. Para alterar sua senha,
                            acesse as configurações da sua conta Google.
                        </p>
                        <button
                            className="cpp-btn-back-full"
                            onClick={() => navigate('/student-area')}
                        >
                            Voltar para Área do Aluno
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (success) {
        return (
            <div className="cpp-page">
                <div className="cpp-container">
                    <div className="cpp-card">
                        <div className="cpp-icon-success">
                            <CheckCircle size={48} />
                        </div>
                        <h2>Senha alterada com sucesso!</h2>
                        <p className="cpp-success-message">
                            Todas as suas sessões foram encerradas por segurança.
                            Você será redirecionado para fazer login novamente.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="cpp-page">
            <header className="cpp-header">
                <button className="cpp-btn-back" onClick={() => navigate('/student-area')}>
                    <ArrowLeft size={20} />
                    Voltar
                </button>
                <div className="cpp-header-logo">
                    <h1>GRAIA</h1>
                </div>
            </header>

            <div className="cpp-container">
                <div className="cpp-card">
                    <h2>Alterar Senha</h2>
                    <p className="cpp-subtitle">
                        Por segurança, você será desconectado de todos os dispositivos
                    </p>

                    {error && (
                        <div className="cpp-alert cpp-alert-error">
                            <AlertCircle size={16} />
                            <span>{error}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="cpp-form">
                        <div className="cpp-field">
                            <label className="cpp-label">Senha Atual</label>
                            <div className="cpp-input-wrapper">
                                <Lock size={16} className="cpp-input-icon" />
                                <input
                                    type="password"
                                    className="cpp-input"
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    placeholder="Digite sua senha atual"
                                    required
                                    disabled={loading}
                                    autoFocus
                                />
                            </div>
                        </div>

                        <div className="cpp-field">
                            <label className="cpp-label">Nova Senha</label>
                            <div className="cpp-input-wrapper">
                                <Lock size={16} className="cpp-input-icon" />
                                <input
                                    type="password"
                                    className="cpp-input"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="Mínimo 6 caracteres"
                                    required
                                    disabled={loading}
                                    minLength={6}
                                />
                            </div>
                        </div>

                        <div className="cpp-field">
                            <label className="cpp-label">Confirmar Nova Senha</label>
                            <div className="cpp-input-wrapper">
                                <Lock size={16} className="cpp-input-icon" />
                                <input
                                    type="password"
                                    className="cpp-input"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Digite a senha novamente"
                                    required
                                    disabled={loading}
                                    minLength={6}
                                />
                            </div>
                        </div>

                        <div className="cpp-warning">
                            <AlertCircle size={18} />
                            <p>
                                Ao trocar sua senha, você será desconectado de todos os
                                dispositivos e precisará fazer login novamente.
                            </p>
                        </div>

                        <button
                            type="submit"
                            className="cpp-btn-submit"
                            disabled={loading}
                        >
                            {loading ? 'Alterando...' : 'Alterar Senha'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ChangePasswordPage;